import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatMubouScore, getScoreMagnitude } from "../../utils/scoreDisplay";
import {
  faRobot, faFire, faHeart, faChartLine,
  faSeedling, faBolt, faTrophy, faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";

const SHARE_API_URL = process.env.REACT_APP_SHARE_API_URL || "";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/* ── スコア表示 & ソートヘルパー ── */
const formatScoreDisplay = (goal) => formatMubouScore(goal);

/* ── アニメーション ── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(255, 215, 0, 0.3); }
  50%      { box-shadow: 0 0 20px rgba(255, 215, 0, 0.6); }
`;

/* ── レイアウト ── */
const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(-45deg, #0f0c29, #302b63, #24243e, #1a1a2e);
  background-size: 400% 400%;
  animation: ${gradientShift} 12s ease infinite;
  padding: 40px 16px 60px;

  @media (max-width: 768px) {
    padding: 24px 8px 40px;
  }
`;

const Container = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const Title = styled.h1`
  text-align: center;
  font-size: clamp(1.4rem, 4.5vw, 2.2rem);
  font-weight: 900;
  background: linear-gradient(90deg, #ffd700, #ff8c00, #ffd700);
  background-size: 200% auto;
  animation: ${shimmer} 3s linear infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 8px;
`;

const SubTitle = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.45);
  font-size: clamp(0.8rem, 2vw, 0.9rem);
  margin-bottom: 28px;
  animation: ${fadeInUp} 0.5s ease 0.1s both;
`;

/* ── タイトルロウ ── */
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-bottom: 8px;
`;

const TitleIconBadge = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #ffd700, #ff8c00);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(255, 215, 0, 0.4);
  flex-shrink: 0;
`;

/* ── タブ（横スクロール式） ── */
const TabScrollWrap = styled.div`
  position: relative;
  margin-bottom: 28px;
  animation: ${fadeInUp} 0.5s ease 0.15s both;

  /* 右端フェードアウト（スクロール可能を示す） */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 32px;
    height: calc(100% - 8px);
    background: linear-gradient(to right, transparent, rgba(15,12,41,0.85));
    pointer-events: none;
    border-radius: 0 50px 50px 0;
  }
`;

const TabRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0 4px 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 50px;
  border: 2px solid ${(props) =>
    props.$active ? "#ffd700" : "rgba(255,255,255,0.2)"};
  background: ${(props) =>
    props.$active ? "rgba(255,215,0,0.15)" : "rgba(255,255,255,0.05)"};
  color: ${(props) =>
    props.$active ? "#ffd700" : "rgba(255,255,255,0.55)"};
  font-weight: 700;
  font-size: 0.85rem;
  white-space: nowrap;
  flex-shrink: 0;
  box-sizing: border-box;
  line-height: 1;
  cursor: pointer;
  transition: all 0.25s;
  &:hover {
    border-color: #ffd700;
    color: #ffd700;
    background: rgba(255, 215, 0, 0.1);
  }
`;

/* ── 空状態アイコン ── */
const EmptyIconWrap = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

/* ── ランクカード ── */
const RankCard = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 18px;
  margin-bottom: 10px;
  overflow: hidden;
  background: ${(props) => {
    if (props.$rank === 1)
      return "linear-gradient(135deg, rgba(255,215,0,0.2) 0%, rgba(255,236,128,0.1) 100%)";
    if (props.$rank === 2)
      return "linear-gradient(135deg, rgba(192,192,192,0.15) 0%, rgba(232,232,232,0.08) 100%)";
    if (props.$rank === 3)
      return "linear-gradient(135deg, rgba(205,127,50,0.15) 0%, rgba(232,192,122,0.08) 100%)";
    return "rgba(255,255,255,0.04)";
  }};
  border: 1px solid ${(props) => {
    if (props.$rank === 1) return "rgba(255,215,0,0.4)";
    if (props.$rank === 2) return "rgba(192,192,192,0.3)";
    if (props.$rank === 3) return "rgba(205,127,50,0.3)";
    return "rgba(255,255,255,0.08)";
  }};
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.25s;
  animation: ${fadeInUp} 0.4s ease both;

  &:hover {
    transform: translateX(6px);
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 215, 0, 0.4);
  }

  @media (max-width: 768px) {
    padding: 12px 12px;
  }
`;

const TopRankCard = styled(RankCard)`
  box-shadow: 0 0 12px rgba(255, 215, 0, 0.3);
  animation: ${fadeInUp} 0.4s ease both, ${pulseGlow} 3s ease-in-out infinite;
`;

const RankNumber = styled.div`
  font-size: ${(props) => (props.$rank <= 3 ? "26px" : "16px")};
  font-weight: 900;
  min-width: 36px;
  flex-shrink: 0;
  text-align: center;
  color: ${(props) => {
    if (props.$rank === 1) return "#ffd700";
    if (props.$rank === 2) return "#c0c0c0";
    if (props.$rank === 3) return "#cd7f32";
    return "rgba(255,255,255,0.35)";
  }};
`;

const RankInfo = styled.div`
  flex: 1;
  margin-left: 12px;
  min-width: 0;
  overflow: hidden;
`;

const RankTitle = styled.div`
  font-weight: 700;
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RankMeta = styled.div`
  font-size: clamp(0.7rem, 1.8vw, 0.78rem);
  color: rgba(255, 255, 255, 0.4);
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RankScore = styled.div`
  font-weight: 900;
  font-size: clamp(0.85rem, 2.5vw, 1rem);
  background: linear-gradient(90deg, #ffd700, #ff8c00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-left: 10px;
  text-align: right;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: keep-all;
  line-height: 1.12;
  max-width: 42%;
  flex-shrink: 1;
`;

/* ── ボタン ── */
const MoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 300px;
  margin: 24px auto;
  padding: 12px 24px;
  border-radius: 50px;
  border: 2px solid rgba(255, 255, 255, 0.25);
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 700;
  font-size: clamp(0.85rem, 2vw, 0.95rem);
  box-sizing: border-box;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.25s;
  animation: ${fadeInUp} 0.5s ease 0.4s both;
  &:hover {
    border-color: #ffd700;
    color: #ffd700;
    transform: translateY(-2px);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    &:hover {
      transform: none;
      border-color: rgba(255, 255, 255, 0.25);
      color: rgba(255, 255, 255, 0.6);
    }
  }
`;

/* ── ローディング / 空状態 ── */
const StatusText = styled.div`
  text-align: center;
  padding: 50px 20px;
  color: rgba(255, 255, 255, 0.4);
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  animation: ${fadeInUp} 0.5s ease both;
`;

/* ── タブ定義 ── */
const TAB_CONFIG = [
  { key: "reality_score",    icon: faSeedling,        label: "進み始めた夢" },
  { key: "support_count",    icon: faHeart,           label: "応援" },
  { key: "engagement_score", icon: faChartLine,       label: "盛り上がり" },
  { key: "ai_score",         icon: faRobot,           label: "AI判定" },
  { key: "no_way_count",     icon: faFire,            label: "無謀投票" },
  { key: "created_at",       icon: faBolt,            label: "新着" },
];

const PAGE_SIZE = 10;

function RankingPage() {
  const [rankings, setRankings] = useState([]);
  const [sortBy, setSortBy] = useState("reality_score");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const fetchRanking = async (pageNum, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const limit = PAGE_SIZE;
      const offset = (pageNum - 1) * PAGE_SIZE;

      if (sortBy === "ai_score") {
        // AI無謀度: 全件取得して単位ランク×スコアで正しくソート
        const res = await axios.get(`${API_BASE_URL}/api/goals/`, {
          params: { ordering: "-ai_score", limit: 100 },
        });
        const sorted = [...res.data].sort(
          (a, b) => getScoreMagnitude(b) - getScoreMagnitude(a)
        );
        const paged = sorted.slice(offset, offset + limit);
        if (append) {
          setRankings((prev) => [...prev, ...paged]);
        } else {
          setRankings(paged);
        }
        setHasMore(paged.length >= PAGE_SIZE);
      } else if (SHARE_API_URL && sortBy !== "engagement_score" && sortBy !== "reality_score") {
        // その他: Share APIから取得
        const res = await axios.get(`${SHARE_API_URL}/api/ranking`, {
          params: { sort: sortBy, limit, offset },
        });
        const data = res.data;
        if (append) {
          setRankings((prev) => [...prev, ...data]);
        } else {
          setRankings(data);
        }
        setHasMore(data.length >= PAGE_SIZE);
      } else {
        // フォールバック: メインAPI
        const ordering =
          sortBy === "created_at" ? "-created_at"
          : sortBy === "engagement_score" ? "-engagement_score"
          : sortBy === "reality_score" ? "-reality_score"
          : sortBy === "support_count" ? "-support_count"
          : `-no_way_count`;
        const res = await axios.get(`${API_BASE_URL}/api/goals/`, {
          params: { ordering, limit, offset },
        });
        const data = res.data;
        if (append) {
          setRankings((prev) => [...prev, ...data]);
        } else {
          setRankings(data);
        }
        setHasMore(data.length >= PAGE_SIZE);
      }
    } catch (err) {
      console.log("Ranking fetch failed:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchRanking(1, false);
  }, [sortBy]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchRanking(nextPage, true);
  };

  const getScoreLabel = (item) => {
    if (sortBy === "ai_score") return formatScoreDisplay(item);
    if (sortBy === "engagement_score") return `${item.comment_count || 0}件`;
    if (sortBy === "reality_score") {
      if (item.latest_progress_percentage != null) return `${item.latest_progress_percentage}%`;
      return `${item.progress_count || 0}回`;
    }
    if (sortBy === "no_way_count") return `${item.no_way_count || 0}`;
    if (sortBy === "support_count") return `${item.support_count || 0}`;
    if (sortBy === "created_at")
      return new Date(item.created_at).toLocaleDateString();
    return `${item.no_way_count || 0}`;
  };

  return (
    <PageWrapper>
      <Container>
        <TitleRow>
          <TitleIconBadge>
            <FontAwesomeIcon icon={faTrophy} color="#1a1a2e" size="lg" />
          </TitleIconBadge>
          <Title>動き始めた夢ランキング</Title>
        </TitleRow>
        <SubTitle>応援、進捗、コメント。少しでも前に進んだ夢を見つけよう</SubTitle>

        <TabScrollWrap>
          <TabRow>
            {TAB_CONFIG.map(({ key, icon, label }) => (
              <Tab
                key={key}
                $active={sortBy === key}
                onClick={() => setSortBy(key)}
              >
                <FontAwesomeIcon icon={icon} size="xs" />
                {label}
              </Tab>
            ))}
          </TabRow>
        </TabScrollWrap>

        {loading ? (
          <StatusText>読み込み中...</StatusText>
        ) : rankings.length === 0 ? (
          <StatusText>
            <EmptyIconWrap>
              <FontAwesomeIcon icon={faMagnifyingGlass} color="rgba(255,255,255,0.3)" size="xl" />
            </EmptyIconWrap>
            まだ誰も夢を宣言していない。
            <br />
            最初の挑戦者になれ。
          </StatusText>
        ) : (
          <>
            {rankings.map((item, index) => {
              const Card = index === 0 ? TopRankCard : RankCard;
              return (
                <Card
                  key={item.id}
                  $rank={index + 1}
                  onClick={() => navigate(`/boards/${item.id}`)}
                >
                  <RankNumber $rank={index + 1}>{index + 1}</RankNumber>
                  <RankInfo>
                    <RankTitle>{item.title}</RankTitle>
                    {sortBy === "reality_score" ? (
                      <RankMeta>
                        {item.journey_summary ||
                          item.latest_progress_note ||
                          `進捗 ${item.progress_count || 0}回 / コメント ${item.comment_count || 0}件`}
                      </RankMeta>
                    ) : item.comment ? (
                      <RankMeta>AI: {item.comment}</RankMeta>
                    ) : null}
                  </RankInfo>
                  <RankScore>{getScoreLabel(item)}</RankScore>
                </Card>
              );
            })}
            {hasMore && (
              <MoreButton onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? "読み込み中..." : "もっと見る"}
              </MoreButton>
            )}
          </>
        )}
      </Container>
    </PageWrapper>
  );
}

export default RankingPage;

